import APP from 'stores/APP';
import * as MENUS from 'stores/menu';
import security from 'stores/security';
import type { UserProcessStatus } from 'types/customer';

const ENABLE = true;
const DISABLED = false;

// 个人中心各模块类型, 具体含义可以参考getBlocksMap
export type DashboardModuleType =
    | 'open'
    | 'continue'
    | 'resubmit'
    | 'deposit'
    | 'transfer'
    | 'report'
    | 'withdraw'
    | 'security'
    | 'account'
    | 'download'
    | 'fundingRecords'
    | 'syep';
// 每一个dashboard模块需要的配置类型
export interface DashboardItemConfigType {
    key: DashboardModuleType;
    title: string; // 模块在个人中心dashboard的展示标题
    desc: string; // 对模块的详细说明
    link?: string; // 模块对应的路由地址
    className: string; // 模块需要设置的className
    warning?: string; // 模块右上角的警告提示
    external?: boolean; // 是否是针对外部的链接
    eventName?: string; // 点击模块需要埋点, 埋点对应的eventName, 并不是所有的模块都有加埋点
    hidden?: boolean; // 是否隐藏该模块
}

/**
 * @description 获取所有模块的配置
 */
export function getBlocksMap(): Record<DashboardModuleType, DashboardItemConfigType> {
    const { isIRAAccount } = security;

    return {
        open: {
            key: 'open',
            title: __('立即开户'),
            desc: __('立即开户'),
            link: '/accounts/open',
            className: 'item-open',
            external: true,
            eventName: '开户模块'
        },
        continue: {
            key: 'continue',
            title: __('继续开户'),
            desc: __('点击继续开户'),
            link: '/accounts/open',
            className: 'item-open',
            external: true
        },
        resubmit: {
            key: 'resubmit',
            title: __('重新提交'),
            desc: __('重新提交'),
            link: `/accounts/modify/${security.currentCustomerId}`,
            className: 'item-resubmit',
            warning: __('Need Update'),
            external: true
        },
        deposit: {
            key: 'deposit',
            title: isIRAAccount ? __('Contribution') : MENUS.DEPOSIT,
            desc: isIRAAccount
                ? __('View funding instructions and complete contribution notification')
                : __('查看入金指引，填写存款通知'),
            link: '/deposit',
            className: 'item-deposit',
            eventName: '入金模块'
        },
        transfer: {
            key: 'transfer',
            title: MENUS.TRANSFER,
            desc: __('零成本转移持仓'),
            link: '/transfer',
            className: 'item-transfer',
            eventName: '转户模块'
        },
        report: {
            key: 'report',
            title: MENUS.REPORT,
            desc: __('查询账户交易详情'),
            link: '/report',
            className: 'item-report',
            eventName: '账单模块'
        },
        withdraw: {
            key: 'withdraw',
            title: isIRAAccount ? __('Distribution') : MENUS.WITHDRAW,
            desc: isIRAAccount ? __('Request Distribution') : __('提交出金申请'),
            link: '/withdraw',
            className: 'item-withdraw',
            eventName: '出金模块'
        },
        security: {
            key: 'security',
            title: MENUS.SECURITY,
            desc: __('提升账户安全性'),
            link: '/security',
            className: 'item-security',
            eventName: '安全模块'
        },
        account: {
            key: 'account',
            title: MENUS.ACCOUNT,
            desc: __('更新个人资料'),
            link: '/account',
            className: 'item-account',
            eventName: '账户设置模块',
            hidden: !security.isMarscoAccount || !security.isAccountOpened
        },
        download: {
            key: 'download',
            title: MENUS.DOWNLOAD,
            desc: __('下载APP'),
            link: APP.isMobile ? '/dlapp' : '/download',
            className: 'item-download',
            external: true,
            eventName: '下载模块',
            // app内隐藏下载
            hidden: APP.isTiger
        },
        fundingRecords: {
            key: 'fundingRecords',
            title: MENUS.FUNDINGRECORDS,
            desc: __('出入金记录'),
            link: '/funding-records',
            className: 'item-funding-records',
            eventName: '出入金记录模块',
            // 已开户或者账户审核中的marsco可以展示出入金模块, 否则隐藏
            hidden: !(
                security.isMarscoAccount &&
                (security.isAccountOpened || security.currentProcessStatus === 'AUDITING')
            )
        },
        syep: {
            key: 'syep',
            title: MENUS.NAV_SYEP,
            desc: __('Earn passive income from your existing positions'),
            link: '/syep',
            className: 'item-syep',
            // 上手是marsco的已开户用户才展示
            hidden: !(security.isMarscoAccount && security.isAccountOpened)
        }
    };
}

export type StatusMapItemType = Array<[DashboardModuleType, boolean]>;

const UNACCOUNT_BLOCKS: StatusMapItemType = [
    ['open', ENABLE],
    ['security', ENABLE],
    ['download', ENABLE],
    ['deposit', DISABLED],
    ['transfer', DISABLED],
    ['report', DISABLED],
    ['withdraw', DISABLED]
];

const CONTINUE_BLOCKS: StatusMapItemType = [
    ['continue', ENABLE],
    ['security', ENABLE],
    ['download', ENABLE],
    ['deposit', DISABLED],
    ['transfer', DISABLED],
    ['report', DISABLED],
    ['withdraw', DISABLED]
];

const INVALID_BLOCKS: StatusMapItemType = [
    ['resubmit', ENABLE],
    ['security', ENABLE],
    ['download', ENABLE],
    ['deposit', DISABLED],
    ['transfer', DISABLED],
    ['report', DISABLED],
    ['withdraw', DISABLED]
];

const getAuditingBlock: () => StatusMapItemType = () => {
    const arr: StatusMapItemType = [
        ['security', ENABLE],
        ['download', ENABLE],
        ['continue', ENABLE]
    ];

    // marsco 审核中可以入金, 但是 IRA 账户不展示入金入口
    if (security.isMarscoAccount && !security.isIRAAccount) {
        arr.push(['deposit', ENABLE]);
    }

    arr.push(['transfer', DISABLED], ['report', DISABLED], ['withdraw', DISABLED]);

    return arr;
};

const OPENED_BLOCKS: StatusMapItemType = [
    ['deposit', ENABLE],
    ['transfer', ENABLE],
    ['report', ENABLE],
    ['download', ENABLE],
    ['security', ENABLE],
    ['withdraw', DISABLED],
    ['fundingRecords', ENABLE],
    ['account', ENABLE],
    ['syep', ENABLE]
];

const FUNDED_BLOCKS: StatusMapItemType = [
    ['deposit', ENABLE],
    ['transfer', ENABLE],
    ['report', ENABLE],
    ['security', ENABLE],
    ['report', ENABLE],
    ['withdraw', ENABLE],
    ['fundingRecords', ENABLE],
    ['account', ENABLE],
    ['syep', ENABLE]
];

const FDFUNDED_BLOCKS: StatusMapItemType = [
    ['deposit', ENABLE],
    ['transfer', ENABLE],
    ['report', ENABLE],
    ['security', ENABLE],
    ['download', ENABLE],
    ['withdraw', ENABLE],
    ['fundingRecords', ENABLE],
    ['account', ENABLE],
    ['syep', ENABLE]
];

const CLOSED_BLOCKS: StatusMapItemType = [
    ['security', ENABLE],
    ['download', ENABLE],
    ['deposit', DISABLED],
    ['transfer', DISABLED],
    ['report', DISABLED]
];

export const getStatusMap: () => Record<UserProcessStatus | 'FDFUNDED', StatusMapItemType> = () => ({
    UNACCOUNT: UNACCOUNT_BLOCKS, // 未开户（已注册）
    FACE: CONTINUE_BLOCKS, // 人脸识别
    CONTINUE: CONTINUE_BLOCKS, // 继续开户
    INVALID: INVALID_BLOCKS, // 修改资料
    AUDITING: getAuditingBlock(), // 审核中
    ACTIVATING: getAuditingBlock(), // 激活中
    OPENED: OPENED_BLOCKS, // 已经开户成功
    FUNDED: FUNDED_BLOCKS, // 已入金
    FDFUNDED: FDFUNDED_BLOCKS, // FD入金
    CLOSED: CLOSED_BLOCKS // 账户已注销
});
